import React, { useState, useEffect, useCallback } from 'react'
import { api } from '../../utils/api'

const STATUS_LABELS = {
  active: 'Active',
  inactive: 'Inactive',
  pending_review: 'Pending Review'
}

const STATUS_BADGE_CLASS = {
  active: 'badge-success',
  inactive: 'badge-neutral',
  pending_review: 'badge-warning'
}

function Collections() {
  const [collections, setCollections] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editingId, setEditingId] = useState(null)
  const [editTags, setEditTags] = useState('')
  const [actionLoading, setActionLoading] = useState(null)

  const fetchCollections = useCallback(async () => {
    try {
      setError(null)
      const data = await api.get('/api/collection-mappings')
      setCollections(data.collections || [])
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchCollections()
  }, [fetchCollections])

  const updateStatus = async (id, status) => {
    setActionLoading(id)
    try {
      await api.put(`/api/collection-mappings/${id}`, { status })
      await fetchCollections()
    } catch (err) {
      setError(err.message)
    } finally {
      setActionLoading(null)
    }
  }

  const deleteCollection = async (id, handle) => {
    if (!window.confirm(`Delete collection "${handle}"? This cannot be undone.`)) return
    setActionLoading(id)
    try {
      await api.delete(`/api/collection-mappings/${id}`)
      await fetchCollections()
    } catch (err) {
      setError(err.message)
    } finally {
      setActionLoading(null)
    }
  }

  const startEditTags = (collection) => {
    setEditingId(collection.managed_collection_id)
    setEditTags(collection.tags.join(', '))
  }

  const saveTags = async (id) => {
    setActionLoading(id)
    try {
      const tags = editTags
        .split(',')
        .map(t => t.trim())
        .filter(Boolean)
        .map(tag => ({ tag }))
      await api.put(`/api/collection-mappings/${id}`, { tags })
      setEditingId(null)
      await fetchCollections()
    } catch (err) {
      setError(err.message)
    } finally {
      setActionLoading(null)
    }
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditTags('')
  }

  if (loading) {
    return <div className="loading-spinner" style={{ padding: 'var(--space-8)', textAlign: 'center' }}>Loading collections...</div>
  }

  const pendingCollections = collections.filter(c => c.status === 'pending_review')
  const activeCollections = collections.filter(c => c.status === 'active')
  const inactiveCollections = collections.filter(c => c.status === 'inactive')
  const ignoredCollections = collections.filter(c => c.status === 'ignored')

  return (
    <div>
      {error && (
        <div style={{
          padding: 'var(--space-3) var(--space-4)',
          background: 'var(--color-bg-error)',
          color: 'var(--crimson)',
          borderRadius: 'var(--radius-sm)',
          marginBottom: 'var(--space-4)',
          fontSize: 14
        }}>
          {error}
          <button
            onClick={() => setError(null)}
            style={{ marginLeft: 'var(--space-3)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--crimson)', fontWeight: 600 }}
          >
            Dismiss
          </button>
        </div>
      )}

      <CollectionSection
        title="Pending Review"
        count={pendingCollections.length}
        collections={pendingCollections}
        badgeClass="badge-warning"
        editingId={editingId}
        editTags={editTags}
        setEditTags={setEditTags}
        actionLoading={actionLoading}
        onActivate={(id) => updateStatus(id, 'active')}
        onIgnore={(id) => updateStatus(id, 'ignored')}
        onDelete={deleteCollection}
        onStartEdit={startEditTags}
        onSaveTags={saveTags}
        onCancelEdit={cancelEdit}
        emptyMessage="No collections awaiting review. The daily discovery job checks Shopify for new smart collections."
      />

      <CollectionSection
        title="Active Collections"
        count={activeCollections.length}
        collections={activeCollections}
        badgeClass="badge-success"
        editingId={editingId}
        editTags={editTags}
        setEditTags={setEditTags}
        actionLoading={actionLoading}
        onDeactivate={(id) => updateStatus(id, 'inactive')}
        onStartEdit={startEditTags}
        onSaveTags={saveTags}
        onCancelEdit={cancelEdit}
        emptyMessage="No active collections."
      />

      <CollectionSection
        title="Inactive"
        count={inactiveCollections.length}
        collections={inactiveCollections}
        badgeClass="badge-neutral"
        editingId={editingId}
        editTags={editTags}
        setEditTags={setEditTags}
        actionLoading={actionLoading}
        onActivate={(id) => updateStatus(id, 'active')}
        onDelete={deleteCollection}
        onStartEdit={startEditTags}
        onSaveTags={saveTags}
        onCancelEdit={cancelEdit}
        emptyMessage="No inactive collections."
      />

      <IgnoredSection
        collections={ignoredCollections}
        actionLoading={actionLoading}
        onUnignore={(id) => updateStatus(id, 'pending_review')}
      />
    </div>
  )
}

function CollectionSection({
  title,
  count,
  collections,
  editingId,
  editTags,
  setEditTags,
  actionLoading,
  onActivate,
  onDeactivate,
  onIgnore,
  onDelete,
  onStartEdit,
  onSaveTags,
  onCancelEdit,
  emptyMessage
}) {
  return (
    <div style={{ marginBottom: 'var(--space-6)' }}>
      <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 'var(--space-3)', color: 'var(--color-text)' }}>
        {title} ({count})
      </h3>

      {collections.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)', fontSize: 14, fontStyle: 'italic' }}>
          {emptyMessage}
        </p>
      ) : (
        <div style={{
          background: 'white',
          borderRadius: 'var(--radius)',
          border: '1px solid var(--color-border)',
          overflow: 'hidden'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: 'var(--color-bg-secondary)', borderBottom: '1px solid var(--color-border)' }}>
                <th style={thStyle}>Collection</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Shopify ID</th>
                <th style={{ ...thStyle, width: '40%' }}>Tags</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {collections.map(col => (
                <CollectionRow
                  key={col.managed_collection_id}
                  collection={col}
                  isEditing={editingId === col.managed_collection_id}
                  editTags={editTags}
                  setEditTags={setEditTags}
                  isLoading={actionLoading === col.managed_collection_id}
                  onActivate={onActivate}
                  onDeactivate={onDeactivate}
                  onIgnore={onIgnore}
                  onDelete={onDelete}
                  onStartEdit={onStartEdit}
                  onSaveTags={onSaveTags}
                  onCancelEdit={onCancelEdit}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function CollectionRow({
  collection,
  isEditing,
  editTags,
  setEditTags,
  isLoading,
  onActivate,
  onDeactivate,
  onIgnore,
  onDelete,
  onStartEdit,
  onSaveTags,
  onCancelEdit
}) {
  const col = collection
  const id = col.managed_collection_id

  return (
    <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
      <td style={tdStyle}>
        <span style={{ fontWeight: 600 }}>{col.collection_handle}</span>
      </td>
      <td style={tdStyle}>
        <span className={`badge ${col.collection_type === 'smart' ? 'badge-info' : 'badge-neutral'}`}>
          {col.collection_type}
        </span>
      </td>
      <td style={tdStyle}>
        <span style={{ fontFamily: 'monospace', fontSize: 13, color: 'var(--color-text-muted)' }}>
          {col.shopify_collection_id || '-'}
        </span>
      </td>
      <td style={tdStyle}>
        {isEditing ? (
          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
            <input
              type="text"
              value={editTags}
              onChange={(e) => setEditTags(e.target.value)}
              style={{
                flex: 1,
                padding: 'var(--space-1) var(--space-2)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 13
              }}
              placeholder="Tag1, Tag2, Tag3"
              onKeyDown={(e) => {
                if (e.key === 'Enter') onSaveTags(id)
                if (e.key === 'Escape') onCancelEdit()
              }}
            />
            <button onClick={() => onSaveTags(id)} disabled={isLoading} style={btnSmallStyle}>Save</button>
            <button onClick={onCancelEdit} style={{ ...btnSmallStyle, background: 'var(--color-bg-secondary)', color: 'var(--color-text)' }}>Cancel</button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {col.tags.length === 0 ? (
              <span style={{ color: 'var(--color-text-muted)', fontStyle: 'italic' }}>No tags</span>
            ) : (
              col.tags.map(tag => (
                <span key={tag} style={tagStyle}>{tag}</span>
              ))
            )}
          </div>
        )}
      </td>
      <td style={{ ...tdStyle, textAlign: 'right' }}>
        <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
          {!isEditing && (
            <button onClick={() => onStartEdit(col)} style={btnSmallStyle} disabled={isLoading}>
              Edit Tags
            </button>
          )}
          {onActivate && col.status !== 'active' && (
            <button
              onClick={() => onActivate(id)}
              disabled={isLoading}
              style={{ ...btnSmallStyle, background: 'var(--green-onions)', color: 'white' }}
            >
              {isLoading ? '...' : 'Activate'}
            </button>
          )}
          {onIgnore && col.status === 'pending_review' && (
            <button
              onClick={() => onIgnore(id)}
              disabled={isLoading}
              style={{ ...btnSmallStyle, background: 'var(--color-bg-secondary)', color: 'var(--color-text-muted)' }}
            >
              {isLoading ? '...' : 'Ignore'}
            </button>
          )}
          {onDeactivate && col.status === 'active' && (
            <button
              onClick={() => onDeactivate(id)}
              disabled={isLoading}
              style={{ ...btnSmallStyle, background: 'var(--color-bg-secondary)', color: 'var(--color-text)' }}
            >
              {isLoading ? '...' : 'Deactivate'}
            </button>
          )}
          {onDelete && (
            <button
              onClick={() => onDelete(id, col.collection_handle)}
              disabled={isLoading}
              style={{ ...btnSmallStyle, background: 'var(--crimson)', color: 'white' }}
            >
              Delete
            </button>
          )}
        </div>
      </td>
    </tr>
  )
}

function IgnoredSection({ collections, actionLoading, onUnignore }) {
  const [expanded, setExpanded] = useState(false)

  if (collections.length === 0) return null

  return (
    <div style={{ marginBottom: 'var(--space-6)' }}>
      <h3
        onClick={() => setExpanded(!expanded)}
        style={{
          fontSize: 16,
          fontWeight: 600,
          marginBottom: 'var(--space-3)',
          color: 'var(--color-text-muted)',
          cursor: 'pointer',
          userSelect: 'none'
        }}
      >
        {expanded ? '\u25BC' : '\u25B6'} Ignored ({collections.length})
      </h3>

      {expanded && (
        <div style={{
          background: 'white',
          borderRadius: 'var(--radius)',
          border: '1px solid var(--color-border)',
          overflow: 'hidden'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <tbody>
              {collections.map(col => (
                <tr key={col.managed_collection_id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td style={tdStyle}>
                    <span style={{ fontWeight: 600, color: 'var(--color-text-muted)' }}>{col.collection_handle}</span>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ fontFamily: 'monospace', fontSize: 13, color: 'var(--color-text-muted)' }}>
                      {col.shopify_collection_id || '-'}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <button
                      onClick={() => onUnignore(col.managed_collection_id)}
                      disabled={actionLoading === col.managed_collection_id}
                      style={btnSmallStyle}
                    >
                      {actionLoading === col.managed_collection_id ? '...' : 'Unignore'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

const thStyle = {
  padding: 'var(--space-3) var(--space-4)',
  textAlign: 'left',
  fontWeight: 600,
  fontSize: 13,
  color: 'var(--color-text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px'
}

const tdStyle = {
  padding: 'var(--space-3) var(--space-4)',
  verticalAlign: 'middle'
}

const btnSmallStyle = {
  padding: 'var(--space-1) var(--space-3)',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--color-border)',
  background: 'white',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  whiteSpace: 'nowrap'
}

const tagStyle = {
  display: 'inline-block',
  padding: '2px 8px',
  background: 'var(--color-bg-secondary)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 12,
  fontWeight: 500,
  color: 'var(--color-text)'
}

export default Collections
